import { GA4_MEASUREMENT_ID, IS_NOT_PRODUCTION } from '@/init/settings'
import type { AuthUser, Billing, Plan } from '@/models/interfaces'
import { experimentsData } from '@/services/ab'

/**
 * Trigger `Signup` event in Google Tag Manager.
 */
export function register(user: AuthUser): void {
  if (!hasGTM()) {
    return
  }

  setUserID(user.id)
  dataLayer.push({
    event: 'Signup',
  })
}

/**
 * Trigger the `CardSave` / `BillingChange` event in Google Tag Manager.
 */
export function subscribe(
  user: AuthUser | null,
  new_plan: Plan,
  billing: Billing,
): void {
  if (!hasGTM()) {
    return
  }

  let conversionValue = 0
  // This is the value we use on the Google Analytics event
  // in GTM. We don't use `conversionValue` on the Google Analytics
  // event because there's a separate event tracked from the backend
  // (`BillingUpdate`) where we set the actual conversion value.
  let ga4Value = 0
  let plan = ''
  let event = 'CardSave'

  if (new_plan.id === 'plan_monthly') {
    conversionValue = 100
    ga4Value = 1
    plan = 'Monthly'
  } else if (new_plan.id === 'plan_annual') {
    conversionValue = 245
    ga4Value = 1
    plan = 'Annual'
  }

  if (billing.trial_start === null) {
    // We only want to track the event once,
    // and we use trial_start for that - it is
    // null only on the first subscription,
    // once subscribed, trial starts and after
    // that trial_start / trial_end remain unchanged.
    // Set 'TestCardSave' event for shortform users.
    if (user?.email?.trim().endsWith('shortform.com')) {
      event = 'TestCardSave'
    }
    // Set 'TestCardSave' event for non-production environments.
    if (IS_NOT_PRODUCTION) {
      event = 'TestCardSave'
    }
  } else {
    event = 'BillingChange'
  }

  setUserID(billing.user_id)
  dataLayer.push({
    event,
    conversionValue,
    ga4Value,
    plan,
  })
}

/**
 * Get the current client's Google Analytics 4 client ID and call
 * clientIdHandler with it.
 */
export function getGa4ClientId(
  clientIdHandler: (clientId: string) => void,
): void {
  if (!hasGTM()) {
    return
  }
  gtag('get', GA4_MEASUREMENT_ID, 'client_id', clientIdHandler)
}

/**
 * Set the `userId` variable and `user_id` user property in GTM.
 *
 * Note: we also set `user_id` when setting up the tags in Google Tag Manager.
 */
export function setUserID(id: string): void {
  if (!hasGTM()) {
    return
  }
  // `user_id` is a user property in Google Analytics events,
  // and `userId` is a variable we use in the Tag Manager.
  // It should be enough to use `dataLayer.push()` to update
  // these properties, but there are some tags in Google Tag Manager
  // that only use the updated values when we use `gtag('set')`.
  gtag('set', { userId: id, user_id: id })
}

/**
 * Push active AB experiments to the dataLayer for GTM (MS Clarity custom tags).
 * Fires event `ABExperimentsReady` with key `abExperiments`.
 */
export function pushABExperimentsToDataLayer(): void {
  if (!hasGTM()) {
    return
  }
  dataLayer.push({
    event: 'ABExperimentsReady',
    abExperiments: experimentsData(),
  })
}

/**
 * Check if the Google Tag Manager globals (`dataLayer` and `gtag`) exist.
 *
 * These globals are initialized in the index.html file. If they don't exist,
 * it probably means that the user has Google Tag Manager tracking blocked.
 */
export function hasGTM(): boolean {
  return typeof dataLayer === 'object' && typeof gtag === 'function'
}

/**
 * Create the `dataLayer` object, and the `gtag` function.
 *
 * We call this function in `inline.scripts.ts` to inline it
 * in the document's `<head>` during the build process.
 */
export function initializeDataLayer(): void {
  window.dataLayer = window.dataLayer || []
  window.gtag = function (): void {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments)
  }
}

/*
 * Send Google Tag Manager events via dataLayer.
 *
 * Related documentation:
 * - https://developers.google.com/tag-platform/tag-manager/datalayer
 */
export * as GTM from './gtm'
